import { useEffect, useMemo, useState } from "react";

import { Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Combobox } from "@/components/cinch/Combobox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

import {
  useReportShares,
  useSetShares,
  useShareableUsers,
  type ReportSummary,
  type ShareInput,
  type ShareRole,
  type ShareableUser,
} from "../api";

function label(u?: Pick<ShareableUser, "name" | "email"> | undefined): string {
  if (!u) return "Unknown user";
  return u.name ? `${u.name} (${u.email})` : u.email;
}

/** Manage a report's per-user share list (viewer/editor). Only shown to users
 *  who can manage the report (owner/admin). */
export function ShareDialog({
  open,
  onOpenChange,
  report,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: ReportSummary | null;
}) {
  const reportId = report?.id;
  const usersQ = useShareableUsers(open);
  const sharesQ = useReportShares(reportId, open);
  const setShares = useSetShares();
  const [rows, setRows] = useState<ShareInput[]>([]);

  useEffect(() => {
    if (open && sharesQ.data) {
      setRows(sharesQ.data.map((s) => ({ user_id: s.user_id, role: s.role })));
    }
  }, [open, sharesQ.data]);

  const usersById = useMemo(
    () => new Map((usersQ.data ?? []).map((u) => [u.id, u])),
    [usersQ.data],
  );
  const available = useMemo(
    () =>
      (usersQ.data ?? []).filter(
        (u) => u.id !== report?.created_by && !rows.some((r) => r.user_id === u.id),
      ),
    [usersQ.data, rows, report?.created_by],
  );

  const addUser = (userId: string) =>
    setRows((prev) => [...prev, { user_id: userId, role: "viewer" }]);
  const setRole = (userId: string, role: ShareRole) =>
    setRows((prev) => prev.map((r) => (r.user_id === userId ? { ...r, role } : r)));
  const remove = (userId: string) =>
    setRows((prev) => prev.filter((r) => r.user_id !== userId));

  const save = async () => {
    if (!reportId) return;
    try {
      await setShares.mutateAsync({ id: reportId, shares: rows });
      toast.success("Sharing updated");
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error)?.message ?? "Failed to update sharing");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="truncate">Share “{report?.title}”</DialogTitle>
          <DialogDescription>
            Grant specific people access to this report. Viewers can open it; editors can
            also change it. People with the reports.read permission already see every report.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Combobox
            options={available.map((u) => ({
              id: u.id,
              label: u.name || u.email,
              subtitle: u.name ? u.email : undefined,
              keywords: `${u.name ?? ""} ${u.email}`,
            }))}
            value={null}
            onChange={(id) => {
              if (id) addUser(id);
            }}
            placeholder={
              usersQ.isLoading
                ? "Loading people…"
                : available.length
                  ? "Add a person…"
                  : "Everyone is already added"
            }
            searchPlaceholder="Search people…"
            emptyText="No matching people."
            clearable={false}
            disabled={usersQ.isLoading || available.length === 0}
          />

          <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
            {sharesQ.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not shared with anyone yet.</p>
            ) : (
              rows.map((r) => (
                <div
                  key={r.user_id}
                  className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {label(usersById.get(r.user_id))}
                  </span>
                  <Select value={r.role} onValueChange={(v) => setRole(r.user_id, v as ShareRole)}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="editor">Editor</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(r.user_id)}
                    aria-label="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={setShares.isPending}>
            Cancel
          </Button>
          <Button onClick={save} disabled={setShares.isPending || !reportId}>
            {setShares.isPending ? "Saving…" : "Save sharing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
